import { FormEventHandler, useEffect, useMemo, useState } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  FieldErrors,
  SubmitHandler,
  UseFormReturn,
  useForm,
} from "react-hook-form";
import { Toast } from "@sscs/ui";
import { apiErrorToast, validatePassword } from "Utils";
import * as yup from "Utils/yup";
import { useAcceptInviteMutation } from "../../services/invite.api";

type AcceptInviteFormData = {
  firstName: string;
  lastName: string;
  password: string;
  confirmPassword: string;
};

type UseAcceptInviteForm = {
  errors: FieldErrors<AcceptInviteFormData>;
  onSubmit: FormEventHandler<HTMLFormElement>;
  methods: UseFormReturn<AcceptInviteFormData>;
};

const MAX_FIRST_NAME_LENGTH = 150;
const MAX_LAST_NAME_LENGTH = 150;

const schema: yup.SchemaOf<AcceptInviteFormData> = yup.object({
  firstName: yup
    .string()
    .required()
    .max(
      MAX_FIRST_NAME_LENGTH,
      `Max first name length is ${MAX_FIRST_NAME_LENGTH} characters`,
    ),
  lastName: yup
    .string()
    .required()
    .max(
      MAX_LAST_NAME_LENGTH,
      `Max last name length is ${MAX_LAST_NAME_LENGTH} characters`,
    ),
  password: validatePassword().required(),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref("password"), null], "Passwords must match")
    .required(),
});

const useAcceptInviteForm = (
  onSuccess: () => void,
  inviteToken: string,
): UseAcceptInviteForm => {
  const defaultValues = useMemo(
    () => ({
      firstName: "",
      lastName: "",
      password: "",
      confirmPassword: "",
    }),
    [],
  );

  const [hasSubmissionErrors, setHasSubmissionErrors] = useState(false);
  const [acceptInvite] = useAcceptInviteMutation();

  const methods = useForm<AcceptInviteFormData>({
    resolver: yupResolver(schema),
    defaultValues,
  });

  const {
    formState: { errors, isSubmitSuccessful },
    handleSubmit,
    reset,
  } = methods;

  // to reset after a submit, react-hook-form recommend doing so inside a useEffect
  // https://react-hook-form.com/api/useform/reset/
  useEffect(() => {
    // isSubmitSuccessful only check if there were any runtime errors so additional check for submission errors is necessary
    // https://react-hook-form.com/docs/useformstate
    if (!hasSubmissionErrors) {
      reset(defaultValues);
    }
  }, [hasSubmissionErrors, isSubmitSuccessful, reset, defaultValues]);

  const onSubmit: SubmitHandler<AcceptInviteFormData> = async (data) => {
    setHasSubmissionErrors(false);
    try {
      await acceptInvite({
        inviteToken,
        body: {
          first_name: data.firstName,
          last_name: data.lastName,
          password: data.password,
        },
      }).unwrap();
    } catch (error) {
      apiErrorToast(error, "Could not create account");
      setHasSubmissionErrors(true);
      return;
    }

    reset(defaultValues);

    Toast.show({
      title: "Account created",
      type: "success",
    });

    onSuccess();
  };

  return {
    errors,
    methods,
    onSubmit: handleSubmit(onSubmit),
  };
};

export default useAcceptInviteForm;
